import { isAllowedAustraliaPostReturnService } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { getCarrierServiceLabel } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import {
  getAllCarrierServiceCombinations,
  getStallionExpressRoutedCarrierServices,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  USPS_INTERNATIONAL_MAIL_CLASS_LABELS,
  USPS_INTERNATIONAL_MAIL_CLASSES,
  USPS_RETURNS_MAIL_CLASS_LABELS,
  USPS_RETURNS_MAIL_CLASSES,
} from "@redotech/redo-fulfillment-outbound-labels-common/usps-direct-mail-classes";
import {
  EasyPostCarrierType,
  EasypostOAuthStatus,
  type ICarrierAccount,
  RedoCarrierName,
  redoCarrierNameToCarrier,
  UspsOAuthStatus,
} from "@redotech/redo-fulfillment-rate-common/carrier-account";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { t } from "@redotech/redo-i18n";
import { QR_SUPPORTED_CARRIERS } from "@redotech/redo-return-core-common/shipping/qr-code";
import {
  canUseMerchantCarrierAccountsForReturnLabels,
  defaultReturnRateSourceSelection,
  getReturnRateSourceIdentityKey,
  RETURN_RATE_SOURCE_TYPE,
  type ReturnRateSource,
  type ReturnRateSourceSelection,
} from "@redotech/redo-return-core-common/shipping/return-rate-source-selection";
import {
  getReturnSelectableCarrierLabel,
  returnSelectableCarrierGroups,
} from "@redotech/redo-return-core-common/shipping/return-selectable-carriers";
import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  getMerchantFacingProviderName,
  HAPPY_RETURNS_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { MerchantCarrierReturnLabelMode } from "@redotech/redo-return-settings-primitives-common/merchant-carrier-return-label-mode";
import type { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";

type TeamWithReturnDropoffSettings =
  | {
      settings?: {
        shippingCarrierOverride?: string[] | null;
        qrCodeOnlyShipping?: boolean | null;
        returns?: {
          dropoffs?: { provider: string; enabled?: boolean | null }[] | null;
          merchantCarrierReturnLabels?: {
            mode?: MerchantCarrierReturnLabelMode | null;
          } | null;
        } | null;
      } | null;
    }
  | null
  | undefined;

interface ReturnRateSourceCarrierAccountsResult {
  merchantCarrierAccounts?: ICarrierAccount[] | null;
  sendcloudByoaEnabled?: boolean | null;
}

export interface ReturnRateSourceDropoffIntegrations {
  blueYonderEnabled: boolean;
  happyReturnsEnabled: boolean;
}

export interface ReturnRateSourceServiceLevelOption {
  /** Matched verbatim against rate.service at buy time. */
  serviceLevel: string;
  label: string;
}

export type ReturnRateSourceOption = Omit<
  BadgeRedoListItem<ReturnRateSource>,
  "id"
> & {
  id: string;
  tooltip?: string;
  /** Carrier name for circular icon lookup via getCarrierIcon. */
  logoCarrier?: string;
  /** Selectable service levels for this carrier account; absent when the carrier has no static service-level catalog. */
  serviceLevelOptions?: ReturnRateSourceServiceLevelOption[];
  /** All rate.carrier values this checkbox controls; the option's own source carries the first. */
  groupCarriers?: readonly string[];
};

export type AccountServiceLevelSelection =
  | { kind: "none" }
  | { kind: "all" }
  | { kind: "some"; serviceLevels: ReadonlySet<string> };
export type RedoCarrierAccountsChoice = "all" | "specific" | "none";
export type MerchantCarrierAccountsChoice = "all" | "specific" | "none";

const redoRatesSource: ReturnRateSource = {
  type: RETURN_RATE_SOURCE_TYPE.REDO_RATES,
};

const allMerchantCarrierAccountsSource: ReturnRateSource = {
  type: RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS,
};

const selectableDropoffProviderTypes = [
  VIRTUAL_PROVIDER,
  BLUE_YONDER_PROVIDER,
  HAPPY_RETURNS_PROVIDER,
] as const satisfies readonly DropoffProviderType[];

export const redoRatesOption: ReturnRateSourceOption = {
  type: "badge",
  id: getReturnRateSourceIdentityKey(redoRatesSource),
  value: redoRatesSource,
  badge: {
    get text() {
      return t("{{brandName}} carrier accounts", { brandName: "Redo" });
    },
    color: "gray",
  },
};

export const allMerchantCarrierAccountsOption: ReturnRateSourceOption = {
  type: "badge",
  id: getReturnRateSourceIdentityKey(allMerchantCarrierAccountsSource),
  value: allMerchantCarrierAccountsSource,
  badge: {
    get text() {
      return t("All carrier accounts") || "All carrier accounts";
    },
    color: "gray",
  },
};

export function getCarrierAccountRateSourceOptions({
  carrierAccountsResult,
  team,
}: {
  carrierAccountsResult?: ReturnRateSourceCarrierAccountsResult;
  team: TeamWithReturnDropoffSettings;
}): ReturnRateSourceOption[] {
  return [
    ...(canUseRedoCarrierAccountsForReturnLabels(team)
      ? [redoRatesOption, ...getRedoCarrierRateSourceOptions({ team })]
      : []),
    ...getMerchantCarrierAccountRateSourceOptions({
      carrierAccountsResult,
      team,
    }),
  ];
}

/** Mirrors resolveEffectiveCarriers: the admin cap is a hard ceiling, so the picker never offers a carrier the gate would drop. */
function getRedoCarrierPickerAllowedCarriers(
  team: TeamWithReturnDropoffSettings,
): ReadonlySet<string> | undefined {
  const cap = team?.settings?.shippingCarrierOverride ?? [];
  if (cap.length > 0) {
    return new Set(cap);
  }
  return team?.settings?.qrCodeOnlyShipping ? QR_SUPPORTED_CARRIERS : undefined;
}

export function getRedoCarrierRateSourceOptions({
  team,
}: {
  team: TeamWithReturnDropoffSettings;
}): ReturnRateSourceOption[] {
  const allowed = getRedoCarrierPickerAllowedCarriers(team);
  const groups = allowed
    ? returnSelectableCarrierGroups.filter((group) =>
        group.carriers.some((carrier) => allowed.has(carrier)),
      )
    : returnSelectableCarrierGroups;
  return groups.map(({ label, carriers }) => {
    const source: ReturnRateSource = {
      type: RETURN_RATE_SOURCE_TYPE.REDO_CARRIER,
      carrier: carriers[0],
    };
    return {
      type: "badge",
      id: getReturnRateSourceIdentityKey(source),
      value: source,
      badge: { text: label, color: "gray" },
      logoCarrier: label,
      groupCarriers: carriers,
    } satisfies ReturnRateSourceOption;
  });
}

export function getMerchantCarrierAccountRateSourceOptions({
  carrierAccountsResult,
  team,
}: {
  carrierAccountsResult?: ReturnRateSourceCarrierAccountsResult;
  team: TeamWithReturnDropoffSettings;
}): ReturnRateSourceOption[] {
  if (!canUseMerchantCarrierAccountsForReturnLabels(team ?? {})) {
    return [];
  }

  const carrierAccountOptions = getCarrierAccountOptions({
    accounts: carrierAccountsResult?.merchantCarrierAccounts ?? [],
    sendcloudByoaEnabled: carrierAccountsResult?.sendcloudByoaEnabled ?? false,
  });

  return carrierAccountOptions.length > 0
    ? [allMerchantCarrierAccountsOption, ...carrierAccountOptions]
    : [allMerchantCarrierAccountsOption];
}

export function getSpecificMerchantCarrierAccountRateSourceOptions({
  options,
}: {
  options: ReturnRateSourceOption[];
}): ReturnRateSourceOption[] {
  return options.filter(
    (option) =>
      option.value.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT,
  );
}

export function getAvailableReturnRateSourceSelection({
  carrierAccountOptions,
  dropoffOptions,
  rateSourceSelection,
  team,
}: {
  carrierAccountOptions: ReturnRateSourceOption[];
  dropoffOptions: ReturnRateSourceOption[];
  rateSourceSelection?: ReturnRateSourceSelection;
  team: TeamWithReturnDropoffSettings;
}): ReturnRateSourceSelection {
  const selection =
    rateSourceSelection ??
    getDefaultReturnRateSourceSelection({
      carrierAccountOptions,
      dropoffOptions,
      team,
    });
  const availableSourceKeys = new Set(
    [...carrierAccountOptions, ...dropoffOptions].flatMap((option) => [
      option.id,
      ...(option.groupCarriers ?? []).map((carrier) =>
        getReturnRateSourceIdentityKey({
          type: RETURN_RATE_SOURCE_TYPE.REDO_CARRIER,
          carrier,
        }),
      ),
    ]),
  );

  return buildSelection(
    selection.sources.filter((source) =>
      availableSourceKeys.has(getReturnRateSourceIdentityKey(source)),
    ),
  );
}

export function getReturnRateSourceSelectionForUpdates({
  currentRateSourceSelection,
  rateSourceOptionsLoaded,
  rateSourceSelection,
}: {
  currentRateSourceSelection: ReturnRateSourceSelection;
  rateSourceOptionsLoaded: boolean;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection {
  if (!rateSourceOptionsLoaded && rateSourceSelection) {
    return rateSourceSelection;
  }

  return currentRateSourceSelection;
}

export function getDefaultReturnRateSourceSelection({
  carrierAccountOptions,
  dropoffOptions,
  team,
}: {
  carrierAccountOptions: ReturnRateSourceOption[];
  dropoffOptions: ReturnRateSourceOption[];
  team: TeamWithReturnDropoffSettings;
}): ReturnRateSourceSelection {
  return buildSelection([
    ...getDefaultCarrierAccountRateSources({ carrierAccountOptions, team }),
    ...dropoffOptions.map((option) => option.value),
  ]);
}

export function getDropoffRateSourceOptions({
  dropoffIntegrations,
  team,
}: {
  dropoffIntegrations: ReturnRateSourceDropoffIntegrations;
  team: TeamWithReturnDropoffSettings;
}): ReturnRateSourceOption[] {
  return getDropoffProviderOptions(
    getAvailableDropoffProviders({ dropoffIntegrations, team }),
  );
}

export function getRedoCarrierAccountsChoice(
  rateSourceSelection?: ReturnRateSourceSelection,
): RedoCarrierAccountsChoice {
  const redoSources = getRedoRateSources(rateSourceSelection);
  if (
    redoSources.some(
      (source) => source.type === RETURN_RATE_SOURCE_TYPE.REDO_RATES,
    )
  ) {
    return "all";
  }
  return redoSources.length > 0 ? "specific" : "none";
}

export function getMerchantCarrierAccountsChoice(
  rateSourceSelection?: ReturnRateSourceSelection,
): MerchantCarrierAccountsChoice {
  const merchantSources = getMerchantCarrierAccountSources(rateSourceSelection);
  if (
    merchantSources.some(
      (source) =>
        source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS,
    )
  ) {
    return "all";
  }
  return merchantSources.length > 0 ? "specific" : "none";
}

export function getSelectedSpecificRedoCarrierRateSourceOptions({
  options,
  rateSourceSelection,
}: {
  options: ReturnRateSourceOption[];
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceOption[] {
  const selectedCarriers = new Set(
    getRedoRateSources(rateSourceSelection).flatMap((source) =>
      source.type === RETURN_RATE_SOURCE_TYPE.REDO_CARRIER
        ? [source.carrier]
        : [],
    ),
  );
  // A group counts as selected when any member is stored, so a legacy partial
  // selection still renders checked and normalizes on the next toggle.
  return options.filter((option) =>
    [...getOptionRedoCarriers(option)].some((carrier) =>
      selectedCarriers.has(carrier),
    ),
  );
}

export function getSelectedSpecificMerchantCarrierAccountRateSourceOptions({
  options,
  rateSourceSelection,
}: {
  options: ReturnRateSourceOption[];
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceOption[] {
  return getSelectedReturnRateSourceOptions({
    options,
    sources: getMerchantCarrierAccountSources(rateSourceSelection).filter(
      (source) =>
        source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT,
    ),
  });
}

export function getSelectedDropoffRateSourceOptions({
  options,
  rateSourceSelection,
}: {
  options: ReturnRateSourceOption[];
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceOption[] {
  return getSelectedReturnRateSourceOptions({
    options,
    sources: getDropoffSources(rateSourceSelection),
  });
}

export function updateRedoCarrierAccountRateSources({
  choice,
  rateSourceSelection,
}: {
  choice: RedoCarrierAccountsChoice;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection {
  return buildSelection([
    ...getRedoRateSourcesForChoice({ choice, rateSourceSelection }),
    ...getMerchantCarrierAccountSources(rateSourceSelection),
    ...getDropoffSources(rateSourceSelection),
  ]);
}

export type RedoCarriersAllSelection = "all" | "some" | "none";

/** Tri-state for the Select all checkbox above the Redo carrier list. */
export function getRedoCarriersAllSelection({
  options,
  rateSourceSelection,
}: {
  options: ReturnRateSourceOption[];
  rateSourceSelection?: ReturnRateSourceSelection;
}): RedoCarriersAllSelection {
  const selectedCount = getSelectedSpecificRedoCarrierRateSourceOptions({
    options,
    rateSourceSelection,
  }).length;
  if (selectedCount === 0) {
    return "none";
  }
  return selectedCount === options.length ? "all" : "some";
}

export function updateAllRedoCarrierRateSources({
  options,
  selected,
  rateSourceSelection,
}: {
  options: ReturnRateSourceOption[];
  selected: boolean;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection {
  const carriers = selected
    ? options.flatMap((option) => [...getOptionRedoCarriers(option)])
    : [];
  return buildSelection([
    ...carriers.map(
      (carrier): ReturnRateSource => ({
        type: RETURN_RATE_SOURCE_TYPE.REDO_CARRIER,
        carrier,
      }),
    ),
    ...getMerchantCarrierAccountSources(rateSourceSelection),
    ...getDropoffSources(rateSourceSelection),
  ]);
}

export function updateSpecificRedoCarrierRateSource({
  option,
  selected,
  rateSourceSelection,
}: {
  option: ReturnRateSourceOption;
  selected: boolean;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection {
  const groupCarriers = getOptionRedoCarriers(option);
  const redoCarrierSources = getRedoRateSources(rateSourceSelection).filter(
    (source) =>
      source.type === RETURN_RATE_SOURCE_TYPE.REDO_CARRIER &&
      !groupCarriers.has(source.carrier),
  );

  return buildSelection([
    ...redoCarrierSources,
    ...(selected
      ? [...groupCarriers].map(
          (carrier): ReturnRateSource => ({
            type: RETURN_RATE_SOURCE_TYPE.REDO_CARRIER,
            carrier,
          }),
        )
      : []),
    ...getMerchantCarrierAccountSources(rateSourceSelection),
    ...getDropoffSources(rateSourceSelection),
  ]);
}

function getOptionRedoCarriers(option: ReturnRateSourceOption): Set<string> {
  if (option.groupCarriers?.length) {
    return new Set(option.groupCarriers);
  }
  return option.value.type === RETURN_RATE_SOURCE_TYPE.REDO_CARRIER
    ? new Set([option.value.carrier])
    : new Set();
}

export function updateMerchantCarrierAccountRateSources({
  choice,
  rateSourceSelection,
}: {
  choice: MerchantCarrierAccountsChoice;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection {
  return buildSelection([
    ...getRedoRateSources(rateSourceSelection),
    ...getMerchantCarrierAccountSourcesForChoice({
      choice,
      rateSourceSelection,
    }),
    ...getDropoffSources(rateSourceSelection),
  ]);
}

export function updateSpecificMerchantCarrierAccountRateSource({
  option,
  selected,
  rateSourceSelection,
}: {
  option: ReturnRateSourceOption;
  selected: boolean;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection {
  const selectedSources = getMerchantCarrierAccountSources(
    rateSourceSelection,
  ).filter(
    (source) =>
      source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT &&
      getReturnRateSourceIdentityKey(source) !== option.id,
  );

  return buildSelection([
    ...getRedoRateSources(rateSourceSelection),
    ...selectedSources,
    ...(selected ? [option.value] : []),
    ...getDropoffSources(rateSourceSelection),
  ]);
}

export function getAccountServiceLevelSelection({
  option,
  rateSourceSelection,
}: {
  option: ReturnRateSourceOption;
  rateSourceSelection?: ReturnRateSourceSelection;
}): AccountServiceLevelSelection {
  const source = getMerchantCarrierAccountSources(rateSourceSelection).find(
    (candidate) => getReturnRateSourceIdentityKey(candidate) === option.id,
  );
  if (!source) {
    return { kind: "none" };
  }
  const deniedServiceLevels =
    source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT
      ? source.deniedServiceLevels
      : undefined;
  if (!deniedServiceLevels?.length) {
    return { kind: "all" };
  }
  const denied = new Set(deniedServiceLevels);
  const enabledServiceLevels = (option.serviceLevelOptions ?? []).flatMap(
    (serviceLevelOption) =>
      denied.has(serviceLevelOption.serviceLevel)
        ? []
        : [serviceLevelOption.serviceLevel],
  );
  const enabled = new Set(enabledServiceLevels);
  // Denials naming only service levels no longer in the catalog read as all-enabled.
  if (enabled.size > 0 && enabled.size === option.serviceLevelOptions?.length) {
    return { kind: "all" };
  }
  return { kind: "some", serviceLevels: enabled };
}

export function updateAccountAllServiceLevels({
  option,
  selected,
  rateSourceSelection,
}: {
  option: ReturnRateSourceOption;
  selected: boolean;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection {
  return replaceAccountSource({
    option,
    rateSourceSelection,
    source: selected ? option.value : undefined,
  });
}

export function updateAccountServiceLevel({
  option,
  serviceLevel,
  selected,
  rateSourceSelection,
}: {
  option: ReturnRateSourceOption;
  serviceLevel: string;
  selected: boolean;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection {
  if (option.value.type !== RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT) {
    return rateSourceSelection ?? defaultReturnRateSourceSelection();
  }
  const allServiceLevels = (option.serviceLevelOptions ?? []).map(
    (serviceLevelOption) => serviceLevelOption.serviceLevel,
  );
  const current = getAccountServiceLevelSelection({
    option,
    rateSourceSelection,
  });
  const next = new Set(
    current.kind === "all"
      ? allServiceLevels
      : current.kind === "some"
        ? current.serviceLevels
        : [],
  );
  if (selected) {
    next.add(serviceLevel);
  } else {
    next.delete(serviceLevel);
  }

  if (next.size === 0) {
    return replaceAccountSource({ option, rateSourceSelection });
  }
  const deniedServiceLevels = allServiceLevels
    .filter((candidate) => !next.has(candidate))
    .sort();
  return replaceAccountSource({
    option,
    rateSourceSelection,
    source:
      deniedServiceLevels.length === 0
        ? option.value
        : {
            type: RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT,
            carrierAccountId: option.value.carrierAccountId,
            deniedServiceLevels,
          },
  });
}

function replaceAccountSource({
  option,
  rateSourceSelection,
  source,
}: {
  option: ReturnRateSourceOption;
  rateSourceSelection?: ReturnRateSourceSelection;
  source?: ReturnRateSource;
}): ReturnRateSourceSelection {
  const otherSources = getMerchantCarrierAccountSources(
    rateSourceSelection,
  ).filter(
    (candidate) => getReturnRateSourceIdentityKey(candidate) !== option.id,
  );

  return buildSelection([
    ...getRedoRateSources(rateSourceSelection),
    ...otherSources,
    ...(source ? [source] : []),
    ...getDropoffSources(rateSourceSelection),
  ]);
}

function getServiceLevelOptionsForAccount(
  account: ICarrierAccount,
): ReturnRateSourceServiceLevelOption[] | undefined {
  // USPS-direct return-flow rates stamp these label strings as rate.service
  // (returns classes domestically, international classes cross-border).
  if (account.provider === RateProvider.USPS) {
    return [
      ...USPS_RETURNS_MAIL_CLASSES.map(
        (mailClass) => USPS_RETURNS_MAIL_CLASS_LABELS[mailClass],
      ),
      ...USPS_INTERNATIONAL_MAIL_CLASSES.map(
        (mailClass) => USPS_INTERNATIONAL_MAIL_CLASS_LABELS[mailClass],
      ),
    ].map((label) => ({ serviceLevel: label, label }));
  }

  // Only EasyPost rates carry catalog-form service codes; direct providers
  // stamp label-style strings, so those accounts get account-level selection.
  if (account.provider !== RateProvider.EASYPOST) {
    return undefined;
  }
  const carrier = redoCarrierNameToCarrier[account.redoCarrierName];
  if (!carrier) {
    return undefined;
  }
  const combinations =
    account.redoCarrierName === RedoCarrierName.STALLION_EXPRESS
      ? getStallionExpressRoutedCarrierServices()
      : getAllCarrierServiceCombinations().filter(
          (combination) => combination.carrier === carrier,
        );
  if (combinations.length === 0) {
    return undefined;
  }
  // Routed carriers can repeat a service code across carriers; the stored
  // filter is keyed by code alone, so collapse duplicates.
  const seenServiceLevels = new Set<string>();
  return combinations.flatMap((combination) => {
    if (seenServiceLevels.has(combination.service)) {
      return [];
    }
    seenServiceLevels.add(combination.service);
    // Returns only ever quote AusPost's approved return services; hide the rest.
    if (
      combination.carrier === Carriers.AUSTRALIA_POST &&
      !isAllowedAustraliaPostReturnService(combination.service)
    ) {
      return [];
    }
    return [
      {
        serviceLevel: combination.service,
        label:
          getCarrierServiceLabel(combination.carrier, combination.service) ??
          combination.service,
      },
    ];
  });
}

export function updateDropoffRateSource({
  option,
  selected,
  rateSourceSelection,
}: {
  option: ReturnRateSourceOption;
  selected: boolean;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection {
  const selectedSources = getDropoffSources(rateSourceSelection).filter(
    (source) => getReturnRateSourceIdentityKey(source) !== option.id,
  );

  return buildSelection([
    ...getLabelRateSources(rateSourceSelection),
    ...selectedSources,
    ...(selected ? [option.value] : []),
  ]);
}

export function getReturnRateSourceOptionLabel(
  t: (key: string) => string,
  option: ReturnRateSourceOption,
): string {
  if (option.value.type === RETURN_RATE_SOURCE_TYPE.REDO_RATES) {
    return t("Redo carrier accounts");
  }
  if (option.value.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS) {
    return t("All carrier accounts");
  }
  const text = option.badge.text;
  return typeof text === "string" ? text : "";
}

export function getReturnRateSourceOptionTooltip(
  t: (key: string, options?: { id: string }) => string,
  option: ReturnRateSourceOption,
): string | undefined {
  if (option.value.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT) {
    return t("Carrier account ID: {{id}}", {
      id: option.value.carrierAccountId,
    });
  }
  return option.tooltip;
}

export function canUseRedoCarrierAccountsForReturnLabels(
  team: TeamWithReturnDropoffSettings,
): boolean {
  const mode =
    team?.settings?.returns?.merchantCarrierReturnLabels?.mode ??
    MerchantCarrierReturnLabelMode.ONLY_REDO_CARRIERS;
  return mode !== MerchantCarrierReturnLabelMode.ALWAYS;
}

function getMerchantCarrierAccountSourcesForChoice({
  choice,
  rateSourceSelection,
}: {
  choice: MerchantCarrierAccountsChoice;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSource[] {
  switch (choice) {
    case "all":
      return [allMerchantCarrierAccountsSource];
    case "specific": {
      // Default to no accounts selected; the merchant opts service levels in per account.
      return getMerchantCarrierAccountSources(rateSourceSelection).filter(
        (source) =>
          source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT,
      );
    }
    case "none":
      return [];
  }
}

function getSelectedReturnRateSourceOptions({
  options,
  sources,
}: {
  options: ReturnRateSourceOption[];
  sources: ReturnRateSource[];
}): ReturnRateSourceOption[] {
  const optionById = new Map(
    options.map((option) => [option.id, option] as const),
  );

  return sources.map((source) => {
    const key = getReturnRateSourceIdentityKey(source);
    return optionById.get(key) ?? fallbackOptionForSource(source);
  });
}

function buildSelection(
  sources: ReturnRateSource[],
): ReturnRateSourceSelection {
  return { sources };
}

function getDefaultCarrierAccountRateSources({
  carrierAccountOptions,
  team,
}: {
  carrierAccountOptions: ReturnRateSourceOption[];
  team: TeamWithReturnDropoffSettings;
}): ReturnRateSource[] {
  const redoSources = canUseRedoCarrierAccountsForReturnLabels(team)
    ? [redoRatesSource]
    : [];

  const allMerchantCarrierAccountsAvailable = carrierAccountOptions.some(
    (option) => option.id === allMerchantCarrierAccountsOption.id,
  );

  if (!allMerchantCarrierAccountsAvailable) {
    return redoSources;
  }

  return [...redoSources, allMerchantCarrierAccountsSource];
}

function getLabelRateSources(
  rateSourceSelection?: ReturnRateSourceSelection,
): ReturnRateSource[] {
  return (
    rateSourceSelection ?? defaultReturnRateSourceSelection()
  ).sources.filter(
    (source) =>
      source.type === RETURN_RATE_SOURCE_TYPE.REDO_RATES ||
      source.type === RETURN_RATE_SOURCE_TYPE.REDO_CARRIER ||
      source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS ||
      source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT,
  );
}

function getRedoRateSources(
  rateSourceSelection?: ReturnRateSourceSelection,
): ReturnRateSource[] {
  return getLabelRateSources(rateSourceSelection).filter(
    (source) =>
      source.type === RETURN_RATE_SOURCE_TYPE.REDO_RATES ||
      source.type === RETURN_RATE_SOURCE_TYPE.REDO_CARRIER,
  );
}

function getRedoRateSourcesForChoice({
  choice,
  rateSourceSelection,
}: {
  choice: RedoCarrierAccountsChoice;
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSource[] {
  switch (choice) {
    case "all":
      return [redoRatesSource];
    case "specific":
      // Default to no carriers selected; the merchant opts each one in.
      return getRedoRateSources(rateSourceSelection).filter(
        (source) => source.type === RETURN_RATE_SOURCE_TYPE.REDO_CARRIER,
      );
    case "none":
      return [];
  }
}

function getMerchantCarrierAccountSources(
  rateSourceSelection?: ReturnRateSourceSelection,
): ReturnRateSource[] {
  return getLabelRateSources(rateSourceSelection).filter(
    (source) =>
      source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS ||
      source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT,
  );
}

function getDropoffSources(
  rateSourceSelection?: ReturnRateSourceSelection,
): ReturnRateSource[] {
  return (
    rateSourceSelection ?? defaultReturnRateSourceSelection()
  ).sources.filter(
    (source) => source.type === RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER,
  );
}

function getCarrierAccountOptions({
  accounts,
  sendcloudByoaEnabled,
}: {
  accounts: ICarrierAccount[];
  sendcloudByoaEnabled: boolean;
}): ReturnRateSourceOption[] {
  return accounts
    .filter((account) =>
      isAvailableReturnCarrierAccount({ account, sendcloudByoaEnabled }),
    )
    .map((account) => {
      const source: ReturnRateSource = {
        type: RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT,
        carrierAccountId: account.carrierAccountId,
      };
      const serviceLevelOptions = getServiceLevelOptionsForAccount(account);
      return {
        type: "badge",
        id: getReturnRateSourceIdentityKey(source),
        value: source,
        badge: { text: getCarrierAccountLabel(account), color: "gray" },
        tooltip: t("Carrier account ID: {{id}}", {
          id: account.carrierAccountId,
        }),
        logoCarrier: account.redoCarrierName,
        ...(serviceLevelOptions ? { serviceLevelOptions } : {}),
      };
    });
}

function getCarrierAccountLabel(account: ICarrierAccount): string {
  const accountName = account.name.trim();
  if (
    !accountName ||
    isGenericCarrierAccountName(accountName) ||
    accountName === account.redoCarrierName
  ) {
    return account.redoCarrierName;
  }
  return `${accountName} (${account.redoCarrierName})`;
}

function isGenericCarrierAccountName(accountName: string): boolean {
  return accountName.toLocaleLowerCase() === "carrier account";
}

function getDropoffProviderOptions(
  providers: DropoffProviderType[],
): ReturnRateSourceOption[] {
  return providers.map((provider) => {
    const source: ReturnRateSource = {
      type: RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER,
      provider,
    };
    return {
      type: "badge",
      id: getReturnRateSourceIdentityKey(source),
      value: source,
      badge: { text: getMerchantFacingProviderName(provider), color: "gray" },
    };
  });
}

function fallbackOptionForSource(
  source: ReturnRateSource,
): ReturnRateSourceOption {
  return {
    type: "badge",
    id: getReturnRateSourceIdentityKey(source),
    value: source,
    badge: { text: fallbackLabelForSource(source), color: "gray" },
  };
}

function fallbackLabelForSource(source: ReturnRateSource): string {
  switch (source.type) {
    case RETURN_RATE_SOURCE_TYPE.REDO_RATES:
      return t("{{brandName}} carrier accounts", { brandName: "Redo" });
    case RETURN_RATE_SOURCE_TYPE.REDO_CARRIER:
      return getReturnSelectableCarrierLabel(source.carrier);
    case RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS:
      return t("All carrier accounts");
    case RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT:
      return t("Carrier account {{id}}", { id: source.carrierAccountId });
    case RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER:
      return getMerchantFacingProviderName(source.provider);
  }
}

function getAvailableDropoffProviders({
  dropoffIntegrations,
  team,
}: {
  dropoffIntegrations: ReturnRateSourceDropoffIntegrations;
  team: TeamWithReturnDropoffSettings;
}): DropoffProviderType[] {
  const configuredDropoffs = team?.settings?.returns?.dropoffs ?? [];
  return selectableDropoffProviderTypes.filter((provider) => {
    const enabledInShippingSettings =
      provider === HAPPY_RETURNS_PROVIDER ||
      configuredDropoffs.find((dropoff) => dropoff.provider === provider)
        ?.enabled === true;
    return (
      enabledInShippingSettings &&
      isDropoffProviderIntegrationAvailable({ dropoffIntegrations, provider })
    );
  });
}

function isDropoffProviderIntegrationAvailable({
  dropoffIntegrations,
  provider,
}: {
  dropoffIntegrations: ReturnRateSourceDropoffIntegrations;
  provider: (typeof selectableDropoffProviderTypes)[number];
}): boolean {
  switch (provider) {
    case VIRTUAL_PROVIDER:
      return true;
    case BLUE_YONDER_PROVIDER:
      return dropoffIntegrations.blueYonderEnabled;
    case HAPPY_RETURNS_PROVIDER:
      return dropoffIntegrations.happyReturnsEnabled;
  }
}

function isAvailableReturnCarrierAccount({
  account,
  sendcloudByoaEnabled,
}: {
  account: ICarrierAccount;
  sendcloudByoaEnabled: boolean;
}): boolean {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (account.provider) {
    case RateProvider.EASYPOST:
      if (
        (account.easypostCarrierType === EasyPostCarrierType.FEDEX ||
          account.easypostCarrierType ===
            EasyPostCarrierType.FEDEX_SMARTPOST) &&
        !account.mfaComplete
      ) {
        return false;
      }
      return (
        !account.oauthUrl ||
        account.oauthStatus === EasypostOAuthStatus.APPROVED
      );
    case RateProvider.USPS:
      return (
        account.oauthStatus === UspsOAuthStatus.APPROVED &&
        account.contractRatesLinked
      );
    case RateProvider.SENDCLOUD:
      return sendcloudByoaEnabled && account.authenticated;
    default:
      return false;
  }
}
